import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Nullable } from '@dai/common'
import { DropResult } from '@hello-pangea/dnd'
import { isEqual, isNil, uniq, uniqueId } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  DisplaySnapshotFragment,
  GET_DISPLAY,
  GET_PRODUCTS,
  GetDisplayQuery,
  GetDisplayQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  MercurySimpleProductFragment,
  ProductFragment,
  SET_PLANOGRAM,
  SetPlanogramMutation,
  SetPlanogramMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import { ProductHelpers } from '@/utils/products.helpers'

import { EditPlanogramHelpers } from '../utils/edit-planogram.helpers'
import {
  getClipboardUpcs,
  hasShelfClipboard,
  PLANOGRAM_SHELF_CLIPBOARD_KEY,
  readShelfClipboard,
  writeShelfClipboard,
} from '../utils/planogram-shelf-clipboard'
import { useSelectionState } from './use-selection-state'

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  const tagName = target.tagName.toLowerCase()

  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  )
}

export const useEditPlanogramLogic = (displayId: Nullable<string>) => {
  const [editing, setEditing] = useState(false)
  const { t } = useT()

  const [snapshot, _setSnapshot] =
    useState<Nullable<DisplaySnapshotFragment>>(null)

  const [getPlanogram, { loading: loadingPlanogram }] = useLazyQuery<
    GetDisplayQuery,
    GetDisplayQueryVariables
  >(GET_DISPLAY, {
    fetchPolicy: 'network-only',
  })

  const [getProducts, { loading: loadingProducts }] = useLazyQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS)

  const displayData = useQuery<GetDisplayQuery, GetDisplayQueryVariables>(
    GET_DISPLAY,
    {
      variables: {
        id: displayId!,
      },
      skip: !displayId,
    },
  )

  const zipPlanogram = (
    planogramData: GetDisplayQuery,
    productsData: GetProductsQuery,
  ): { id: string; product: ProductFragment }[][] => {
    const plan: { id: string; product: ProductFragment }[][][] =
      planogramData?.display?.planogram?.shapedUpcs?.map(segment =>
        segment.map(shelf =>
          shelf
            .map(
              upc =>
                productsData?.products?.results?.find(
                  product => product.upcA === upc,
                ) || {
                  __typename: 'Product' as const,
                  upcA: upc,
                  id: uniqueId(),
                  name: null,
                  preferredName: t('editPlanogram.unknownProductName'),
                  packagingName: t('editPlanogram.unknownPackaging'),
                  packaging: null,
                  planogramImageUrl:
                    '/static/img/products/unknown-product-planogram-facing.png',
                  marketingImageUrl:
                    '/static/img/products/unknown-product-marketing-facing.png',
                },
            )
            .map(product => ({ id: uniqueId(), product })),
        ),
      ) || []
    if (plan.length) {
      return plan[0]
    }
    return []
  }

  useEffect(() => {
    if (displayId && !originalState.length) {
      const fetchPlanogram = async () => {
        const { data: planogramData } = await getPlanogram({
          variables: { id: displayId },
        })
        const upcs: string[] = uniq(
          snapshot
            ? snapshot.images[0]?.boxSet?.boundingBoxes
                .map(box => box.classifiedLabel)
                .filter((upc): upc is string => !isNil(upc))
            : planogramData?.display?.planogram?.shapedUpcs
                ?.flatMap(segment => segment.flatMap(upc => upc))
                .sort(),
        )
        if (upcs.length > 0) {
          const { data: productsData } = await getProducts({
            variables: {
              filters: {
                upcs: uniq(upcs),
              },
            },
          })
          if (planogramData && productsData) {
            const planogram = zipPlanogram(planogramData, productsData)
            setState(structuredClone(planogram))
            setOriginalState(structuredClone(planogram))
          }
        }
      }
      fetchPlanogram()
    }
  }, [displayId])

  const setStateFromSnapshot = async (snapshot: DisplaySnapshotFragment) => {
    const planogramProducts: string[][] =
      snapshot.planogramSegmentShapedFacings.map(shelf =>
        shelf
          .map(facing => facing.upc)
          .filter(upc => upc !== ProductHelpers.UNKNOWN_UPC),
      )
    const { data: productsData } = await getProducts({
      variables: {
        filters: {
          upcs: uniq(planogramProducts.flat()),
        },
      },
    })
    const planogramFromSnapshot = planogramProducts.map(shelf =>
      shelf.map(facing => {
        const product = productsData?.products?.results?.find(
          product => product.upcA === facing,
        )
        if (product) {
          return {
            id: uniqueId(),
            product,
          }
        } else {
          return {
            id: uniqueId(),
            product: {
              __typename: 'Product' as const,
              upcA: facing,
              id: uniqueId(),
              name: null,
              preferredName: t('editPlanogram.unknownProductName'),
              packagingName: t('editPlanogram.unknownPackaging'),
              packaging: null,
              brand: null,
              planogramImageUrl:
                '/static/img/products/unknown-product-planogram-facing.png',
              marketingImageUrl:
                '/static/img/products/unknown-product-marketing-facing.png',
              commonName: null,
              manufacturer: null,
              category: null,
            },
          }
        }
      }),
    )
    setState(planogramFromSnapshot)
  }

  const [state, setState] = useState<
    { id: string; product: ProductFragment | MercurySimpleProductFragment }[][]
  >([])

  const [originalState, setOriginalState] = useState<
    { id: string; product: ProductFragment | MercurySimpleProductFragment }[][]
  >([])

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    const destinationIndex =
      destination.droppableId === 'new-shelf'
        ? state.length
        : parseInt(destination.droppableId, 10)

    const newState = structuredClone(state)

    if (destinationIndex >= newState.length) {
      newState.push([])
    }

    if (source.droppableId === 'product-source' && productToAdd) {
      const newFacing = {
        id: uniqueId(),
        product: productToAdd,
      }

      if (!newState[destinationIndex]) {
        newState[destinationIndex] = []
      }

      newState[destinationIndex].splice(destination.index, 0, newFacing)

      setState(newState)
      return
    }

    setState(
      EditPlanogramHelpers.handleReorderItem({
        facings: newState,
        source: { row: parseInt(source.droppableId, 10), column: source.index },
        destination: {
          row: destinationIndex,
          column: destination.index,
        },
      }),
    )
  }

  const [modal, setModal] = useState<'add-product' | 'snapshot' | null>(null)

  const {
    selectedShelves,
    selectedItems,
    setProductToAdd,
    clearSelections,
    clearSelectedShelvesAndItems,
    toggleSelectShelf,
    toggleSelectItem,
    productToAdd,
  } = useSelectionState()

  const onDragStart = () => {
    clearSelectedShelvesAndItems()
  }

  const selectedProducts = state.flatMap((shelf, rowIndex) =>
    shelf.filter((_, index) =>
      selectedItems.some(
        ({ rowIndex: itemRowIndex, index: itemIndex }) =>
          itemIndex === index && itemRowIndex === rowIndex,
      ),
    ),
  )

  const matchingPlanogramFacingCount = useMemo(() => {
    const selectedUpcs = uniq(
      selectedProducts.map(facing => facing.product.upcA),
    )

    if (!selectedUpcs.length) {
      return 0
    }

    return state
      .flat()
      .filter(facing => selectedUpcs.includes(facing.product.upcA)).length
  }, [selectedProducts, state])

  const onFacingClick = (shelfIndex: number, columnIndex: number) => {
    toggleSelectItem({ rowIndex: shelfIndex, index: columnIndex })
  }

  const handleDeleteShelves = () => {
    setState(state.filter((_, index) => !selectedShelves.includes(index)))
    clearSelections()
  }

  const handleDeleteProducts = () => {
    setState(
      state
        .map((shelf, rowIndex) =>
          shelf.filter(
            (_, index) =>
              !selectedItems.some(
                item => item.index === index && item.rowIndex === rowIndex,
              ),
          ),
        )
        .filter(shelf => shelf.length > 0),
    )
    clearSelections()
  }

  const handleCopyProducts = () => {
    setState(
      EditPlanogramHelpers.handleDuplicateItems({ items: selectedItems }),
    )
  }

  const [clipboardAvailable, setClipboardAvailable] =
    useState(hasShelfClipboard)

  const clipboardSource = useMemo(() => {
    const clipboard = readShelfClipboard()

    if (!clipboard) {
      return null
    }

    return {
      storeName: clipboard.sourceStoreName,
      displayName: clipboard.sourceDisplayName,
    }
  }, [clipboardAvailable])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === PLANOGRAM_SHELF_CLIPBOARD_KEY) {
        setClipboardAvailable(hasShelfClipboard())
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const createUnknownProduct = useCallback(
    (upc: string): ProductFragment => ({
      __typename: 'Product' as const,
      upcA: upc,
      id: uniqueId(),
      name: null,
      preferredName: t('editPlanogram.unknownProductName'),
      packagingName: t('editPlanogram.unknownPackaging'),
      packaging: null,
      planogramImageUrl:
        '/static/img/products/unknown-product-planogram-facing.png',
      marketingImageUrl:
        '/static/img/products/unknown-product-marketing-facing.png',
    }),
    [t],
  )

  const handleCopyShelves = useCallback(() => {
    if (!editing || selectedShelves.length === 0 || !displayId) {
      return
    }

    const sortedShelfIndices = [...selectedShelves].sort((a, b) => a - b)

    writeShelfClipboard({
      sourceDisplayId: displayId,
      sourceDisplayName: displayData.data?.display?.name || '',
      sourceStoreName: displayData.data?.display?.store?.location?.name || '',
      shelves: sortedShelfIndices.map(shelfIndex => ({
        shelfIndex,
        upcs: state[shelfIndex]?.map(facing => facing.product.upcA) || [],
      })),
    })

    setClipboardAvailable(hasShelfClipboard())
  }, [
    displayData.data?.display?.name,
    displayData.data?.display?.store?.location?.name,
    displayId,
    editing,
    selectedShelves,
    state,
  ])

  const handlePasteShelf = useCallback(async () => {
    if (!editing || selectedShelves.length === 0) {
      return
    }

    const clipboard = readShelfClipboard()

    if (!clipboard) {
      return
    }

    const upcs = getClipboardUpcs(clipboard)

    if (!upcs.length) {
      return
    }

    const productsByUpc = new Map<
      string,
      ProductFragment | MercurySimpleProductFragment
    >()

    state.flat().forEach(facing => {
      productsByUpc.set(facing.product.upcA, facing.product)
    })

    const missingUpcs = uniq(upcs).filter(upc => !productsByUpc.has(upc))

    if (missingUpcs.length > 0) {
      const { data: productsData } = await getProducts({
        variables: {
          filters: {
            upcs: missingUpcs,
          },
        },
      })

      productsData?.products?.results?.forEach(product => {
        productsByUpc.set(product.upcA, product)
      })
    }

    const facings = upcs.map(upc => ({
      id: uniqueId(),
      product: productsByUpc.get(upc) || createUnknownProduct(upc),
    }))

    setState(
      EditPlanogramHelpers.handleAppendFacings({
        targetShelfIndices: selectedShelves,
        facings,
      }),
    )
  }, [
    createUnknownProduct,
    editing,
    getProducts,
    selectedShelves,
    setState,
    state,
  ])

  useEffect(() => {
    if (!editing) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) {
        return
      }

      if (isEditableTarget(event.target) || modal === 'add-product') {
        return
      }

      if (selectedShelves.length === 0) {
        return
      }

      const key = event.key.toLowerCase()

      if (key === 'c') {
        event.preventDefault()
        handleCopyShelves()
        return
      }

      if (key === 'v' && clipboardAvailable) {
        event.preventDefault()
        void handlePasteShelf()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    clipboardAvailable,
    editing,
    handleCopyShelves,
    handlePasteShelf,
    modal,
    selectedShelves.length,
  ])

  const [setPlanogram, { loading: savingChanges }] = useMutation<
    SetPlanogramMutation,
    SetPlanogramMutationVariables
  >(SET_PLANOGRAM)

  const handleSave = () => {
    const finalState = structuredClone(state)
    setPlanogram({
      refetchQueries: ['GetDisplayPlanogramHistory'],
      variables: {
        data: {
          display: {
            assign: displayId,
          },
          segments: {
            create: [
              {
                segmentIndex: 0,
                shelves: {
                  create: state.map((shelf, index) => ({
                    displayShelfIndex: index,
                    segmentShelfIndex: index,
                    planogramfacings: {
                      create: shelf.map((facing, facingIndex) => ({
                        columnIndex: facingIndex,
                        upc: facing.product.upcA,
                        quantity: 1,
                      })),
                    },
                  })),
                },
              },
            ],
          },
        },
      },
    }).then(() => {
      setEditing(false)
      setOriginalState(finalState)
    })
  }

  const setSnapshot = (snapshot: Nullable<DisplaySnapshotFragment>) => {
    _setSnapshot(snapshot)
    if (snapshot) {
      setStateFromSnapshot(snapshot)
    }
  }

  return {
    editing,
    setEditing,
    savingChanges,
    state,
    setState,
    snapshot,
    setSnapshot,
    originalState,
    originalStateIsEqual: isEqual(state, originalState),
    displayName: displayData.data?.display?.name,
    storeName: displayData.data?.display?.store?.location?.name,
    loading: loadingPlanogram || loadingProducts || displayData.loading,
    onDragEnd,
    onDragStart,
    selectedShelves,
    selectedItems,
    clearSelections,
    toggleSelectShelf,
    onFacingClick,
    handleDeleteShelves,
    handleDeleteProducts,
    handleCopyProducts,
    handleCopyShelves,
    handlePasteShelf,
    clipboardAvailable,
    clipboardSource,
    handleSave,
    selectedProducts,
    matchingPlanogramFacingCount,
    productToAdd,
    setProductToAdd,
    modal,
    setModal,
  }
}
